import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore.js';
import { api } from '../lib/api.js';

function getRankTitle(xp: number): string {
  if (xp <= 300) return 'Fresh Spawn';
  if (xp <= 800) return 'Bronze Scrapper';
  if (xp <= 1500) return 'Iron Duelist';
  if (xp <= 3000) return 'Silver Striker';
  if (xp <= 5000) return 'Gold Gladiator';
  if (xp <= 8000) return 'Platinum Predator';
  if (xp <= 12000) return 'Diamond Reaper';
  if (xp <= 18000) return 'Mythic Warrior';
  return 'PvP Grandmaster';
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [adminAllowed, setAdminAllowed] = useState(false);

  useEffect(() => {
    if (!user) {
      setAdminAllowed(false);
      return;
    }

    let cancelled = false;

    api
      .get<{ allowed: boolean }>('/api/admin/access')
      .then(({ data }) => {
        if (!cancelled) setAdminAllowed(data.allowed);
      })
      .catch(() => {
        if (!cancelled) setAdminAllowed(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  const menuItems = [
    { name: 'Home', path: '/home', icon: 'home' },
    { name: 'Courses', path: '/courses', icon: 'school' },
    { name: 'Highlights', path: '/highlights', icon: 'movie' },
    { name: 'Leaderboard', path: '/leaderboard', icon: 'leaderboard' },
    { name: 'Announcements', path: '/announcements', icon: 'campaign' },
    { name: 'Practice Map', path: '/map', icon: 'explore' }
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const avatarSrc =
    user?.avatar_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      user?.username || 'User'
    )}&background=1a1a1a&color=e03c3c&size=120&bold=true&format=svg`;

  const xp = user?.total_xp ?? 0;

  return (
    <aside className="fixed left-0 top-14 h-[calc(100vh-3.5rem)] w-[260px] bg-surface-container-low border-r border-outline-variant flex flex-col py-stack-lg z-40 select-none">

      {user && (
        <Link
          to={`/profile/${user.username}`}
          className="px-6 mb-8 flex items-center gap-3 group hover:opacity-90 transition-opacity"
        >
          <div className="w-10 h-10 rounded-lg overflow-hidden thin-border bg-surface-variant flex-shrink-0">
            <img
              alt="Avatar"
              className="w-full h-full object-cover"
              src={avatarSrc}
            />
          </div>

          <div className="min-w-0">
            <div className="font-button text-button text-on-surface truncate group-hover:text-error transition-colors">
              {user.username}
            </div>
            <div className={`text-[10px] uppercase tracking-widest font-bold ${
              user.is_admin 
                ? 'text-[#00ff00] font-["Share_Tech"]' 
                : 'text-error'
            }`}>
              {user.is_admin ? 'ADMIN' : getRankTitle(xp)}
            </div>
          </div>
        </Link>
      )}

      <nav className="flex-1 px-3 space-y-1">
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-3 font-button text-button transition-colors duration-150 rounded-[4px] ${
                isActive
                  ? 'text-on-surface border-l-2 border-error bg-surface-variant font-semibold'
                  : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high'
              }`}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0"
                }}
              >
                {item.icon}
              </span>
              <span>{item.name}</span>
            </Link>
          );
        })}

        {adminAllowed && (
          <Link
            to="/admin"
            className={`flex items-center gap-3 px-3 py-3 font-button text-button transition-colors duration-150 rounded-[4px] ${
              location.pathname.startsWith('/admin')
                ? 'text-on-surface border-l-2 border-error bg-surface-variant font-semibold'
                : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high'
            }`}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontVariationSettings: location.pathname.startsWith('/admin')
                  ? "'FILL' 1"
                  : "'FILL' 0"
              }}
            >
              admin_panel_settings
            </span>
            <span>Admin</span>
          </Link>
        )}
      </nav>

      <div className="px-4 mt-auto space-y-2">
        <button
          onClick={() => navigate('/map')}
          className="w-full bg-[#e03c3c] hover:brightness-110 active:scale-95 transition-colors text-white py-3 rounded-[4px] font-button text-[12px] uppercase tracking-wider font-bold btn-press-active"
        >
          Download Practice Map
        </button>

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-3 font-button text-button text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high rounded-[4px] transition-colors btn-press-active"
        >
          <span className="material-symbols-outlined">logout</span>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}